import { useCallback, useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Upload, X, ExternalLink, Eye, EyeOff, Send } from "lucide-react";
import { BLOG_CATEGORIES } from "@/lib/blogCategories";
import BlogEmailFlow from "@/components/admin/BlogEmailFlow";
import BlogBlockEditor from "@/components/admin/BlogBlockEditor";
import { blocksToContent, parseContentToBlocks, serializeBlocks, emptyBlock } from "@/lib/blogBlocks";

const empty = {
    slug: "",
    title: "",
    subtitle: "",
    category: "",
    excerpt: "",
    content: "",
    blocks: [emptyBlock("paragraph")],
    cover_id: "",
    cover_alt: "",
    tags: [],
    published: false,
    published_at: "",
    seo_title: "",
    seo_description: "",
};

const slugify = (s) =>
    (s || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 90);

/* Datetime helpers so <input type="datetime-local"> can round-trip the ISO
   strings we store on the server. Input needs "YYYY-MM-DDTHH:mm" without TZ. */
const isoToLocalInput = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return "";
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch { return ""; }
};
const localInputToIso = (v) => {
    if (!v) return "";
    try {
        const d = new Date(v);
        if (Number.isNaN(d.getTime())) return "";
        return d.toISOString();
    } catch { return ""; }
};

export default function AdminBlog() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [tagInput, setTagInput] = useState("");
    const [uploading, setUploading] = useState(false);
    const [emailFlowPost, setEmailFlowPost] = useState(null);

    const load = useCallback(() => api.get("/admin/blog").then((r) => setItems(r.data)), []);
    useEffect(() => { load(); }, [load]);

    const openNew = () => { setEditing({ ...empty, blocks: [emptyBlock("paragraph")] }); setTagInput(""); };
    const openEdit = (p) => {
        // Normalise legacy shape so new fields have safe defaults and the
        // datetime-local input speaks its own format. Existing posts that
        // predate the block editor are lazily parsed into blocks; the raw
        // `content` remains on the server until the admin saves.
        const initialBlocks = (Array.isArray(p.blocks) && p.blocks.length)
            ? p.blocks.map((b) => ({ ...b, id: b.id || `b-${Math.random().toString(36).slice(2, 8)}` }))
            : parseContentToBlocks(p.content || "");
        setEditing({
            ...empty,
            ...p,
            blocks: initialBlocks,
            content: blocksToContent(initialBlocks),
            published_at: isoToLocalInput(p.published_at),
        });
        setTagInput("");
    };

    const setBlocks = (blocks) => {
        // Keep `content` in sync with `blocks` so the existing validation
        // (`content.trim()`) and any legacy fallback continue to work.
        setEditing((s) => ({ ...s, blocks, content: blocksToContent(blocks) }));
    };

    const save = async () => {
        if (!editing.title.trim()) return toast.error("Título obrigatório");
        // `content` is derived from blocks — if it's empty, the article has
        // no usable body.
        const derivedContent = blocksToContent(editing.blocks || []);
        if (!derivedContent.trim()) return toast.error("O artigo tem que ter conteúdo");
        const slug = (editing.slug || slugify(editing.title)).trim();
        if (!slug) return toast.error("Slug inválido");
        // Round-trip the datetime-local input back to ISO (or empty string).
        const payload = {
            ...editing,
            slug,
            content: derivedContent,
            blocks: serializeBlocks(editing.blocks),
            published_at: editing.published_at ? localInputToIso(editing.published_at) : "",
        };
        try {
            if (editing.id) await api.put(`/admin/blog/${editing.id}`, payload);
            else await api.post("/admin/blog", payload);
            toast.success(editing.id ? "Artigo atualizado" : "Artigo criado");
            setEditing(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro ao guardar");
        }
    };

    const togglePublish = async (p) => {
        try {
            await api.put(`/admin/blog/${p.id}`, { ...p, published: !p.published });
            toast.success(!p.published ? "Publicado" : "Despublicado");
            load();
        } catch { toast.error("Erro"); }
    };

    const del = async (id) => {
        if (!window.confirm("Eliminar este artigo? Esta ação é permanente.")) return;
        await api.delete(`/admin/blog/${id}`);
        toast.success("Artigo eliminado");
        load();
    };

    const uploadCover = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload", fd);
            setEditing((s) => ({ ...s, cover_id: data.id }));
            toast.success("Capa carregada");
        } catch { toast.error("Falha no upload"); }
        finally { setUploading(false); }
    };

    const addTag = () => {
        const v = tagInput.trim();
        if (!v) return;
        if (!(editing.tags || []).includes(v)) {
            setEditing((s) => ({ ...s, tags: [...(s.tags || []), v] }));
        }
        setTagInput("");
    };
    const removeTag = (t) => setEditing((s) => ({ ...s, tags: (s.tags || []).filter((x) => x !== t) }));

    return (
        <div className="max-w-5xl">
            <div className="flex justify-between items-center mb-2">
                <h1 className="font-display text-3xl font-bold text-stone-900">Blog</h1>
                <button onClick={openNew} className="btn-copper !py-2 !px-4 text-sm" data-testid="blog-new">
                    <Plus className="w-4 h-4" /> Novo artigo
                </button>
            </div>
            <p className="text-sm text-stone-500 mb-6">
                Publique artigos sobre remodelações para atrair tráfego orgânico. O separador <b>Blog</b> só aparece no menu público quando existir pelo menos um artigo publicado.
            </p>

            <div className="bg-white rounded-xl border border-stone-200 divide-y divide-stone-100">
                {items.map((p) => (
                    <div key={p.id} className="p-4 flex flex-col sm:flex-row gap-3 sm:items-center" data-testid={`blog-item-${p.id}`}>
                        <div className="w-full sm:w-16 h-16 rounded bg-stone-100 overflow-hidden flex-shrink-0">
                            {p.cover_id && <img src={fileUrl(p.cover_id)} alt="" className="w-full h-full object-cover" />}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="font-semibold text-stone-900 truncate">{p.title}</h3>
                                {p.published
                                    ? <span className="text-[10px] uppercase bg-emerald-100 text-emerald-800 rounded-full px-2 py-0.5">Publicado</span>
                                    : <span className="text-[10px] uppercase bg-stone-200 rounded-full px-2 py-0.5">Rascunho</span>}
                                <EmailStatusPill post={p} />
                            </div>
                            <div className="text-xs text-stone-500 truncate">/blog/{p.slug}</div>
                            {p.excerpt && <p className="text-xs text-stone-500 line-clamp-1 mt-1">{p.excerpt}</p>}
                        </div>
                        <div className="flex gap-2 flex-shrink-0 flex-wrap">
                            <button onClick={() => togglePublish(p)} className="text-xs px-3 py-1.5 border border-stone-300 rounded flex items-center gap-1" data-testid={`blog-publish-${p.id}`}>
                                {p.published ? <><EyeOff className="w-3.5 h-3.5" /> Despublicar</> : <><Eye className="w-3.5 h-3.5" /> Publicar</>}
                            </button>
                            {p.published && (
                                <>
                                    <a href={`/blog/${p.slug}`} target="_blank" rel="noopener noreferrer" className="text-xs px-3 py-1.5 border border-stone-300 rounded flex items-center gap-1">
                                        <ExternalLink className="w-3.5 h-3.5" /> Ver
                                    </a>
                                    <button onClick={() => setEmailFlowPost(p)} className="text-xs px-3 py-1.5 border border-[hsl(var(--copper))] text-[hsl(var(--copper))] rounded flex items-center gap-1" data-testid={`blog-send-email-${p.id}`}>
                                        <Send className="w-3.5 h-3.5" /> Enviar por email
                                    </button>
                                </>
                            )}
                            <button onClick={() => openEdit(p)} className="text-xs px-3 py-1.5 border border-stone-300 rounded" data-testid={`blog-edit-${p.id}`}>Editar</button>
                            <button onClick={() => del(p.id)} className="text-xs px-3 py-1.5 border border-red-200 text-red-600 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                    </div>
                ))}
                {items.length === 0 && (
                    <div className="p-10 text-center text-stone-500">
                        Ainda sem artigos. Escreva o primeiro para o <b>Blog</b> começar a aparecer no menu público.
                    </div>
                )}
            </div>

            {editing && (
                <div className="fixed inset-0 bg-stone-950/60 z-50 overflow-y-auto" onClick={() => setEditing(null)}>
                    <div className="min-h-full flex items-start justify-center p-4">
                        <div className="bg-white rounded-xl max-w-3xl w-full my-8" onClick={(e) => e.stopPropagation()}>
                            <div className="p-6 space-y-4">
                                <h2 className="font-display font-bold text-xl">{editing.id ? "Editar artigo" : "Novo artigo"}</h2>

                                <label className="block">
                                    <span className="text-xs uppercase tracking-wider text-stone-500 block mb-1">Título *</span>
                                    <input value={editing.title} onChange={(e) => {
                                        const title = e.target.value;
                                        setEditing((s) => ({ ...s, title, slug: s.slug && s.id ? s.slug : slugify(title) }));
                                    }} className="w-full border border-stone-300 rounded p-2" data-testid="blog-title" />
                                </label>

                                <label className="block">
                                    <span className="text-xs uppercase tracking-wider text-stone-500 block mb-1">Slug (URL)</span>
                                    <div className="flex items-center">
                                        <span className="text-xs text-stone-500 px-2">/blog/</span>
                                        <input value={editing.slug} onChange={(e) => setEditing((s) => ({ ...s, slug: slugify(e.target.value) }))}
                                            className="flex-1 border border-stone-300 rounded p-2 font-mono text-sm" data-testid="blog-slug" />
                                    </div>
                                </label>

                                <div className="grid sm:grid-cols-2 gap-4">
                                    <label className="block">
                                        <span className="text-xs uppercase tracking-wider text-stone-500 block mb-1">Categoria</span>
                                        <select
                                            value={editing.category || ""}
                                            onChange={(e) => setEditing((s) => ({ ...s, category: e.target.value }))}
                                            className="w-full border border-stone-300 rounded p-2 text-sm bg-white"
                                            data-testid="blog-category"
                                        >
                                            <option value="">— Escolher categoria —</option>
                                            {BLOG_CATEGORIES.map((c) => (
                                                <option key={c} value={c}>{c}</option>
                                            ))}
                                            {/* Preserve legacy free-text values so old posts keep their category */}
                                            {editing.category && !BLOG_CATEGORIES.includes(editing.category) && (
                                                <option value={editing.category}>{editing.category} (legado)</option>
                                            )}
                                        </select>
                                        <span className="text-[11px] text-stone-500 mt-1 block">Aparece como <em>eyebrow</em> cor de cobre acima do título.</span>
                                    </label>
                                    <label className="block">
                                        <span className="text-xs uppercase tracking-wider text-stone-500 block mb-1">Subtítulo (deck)</span>
                                        <input value={editing.subtitle || ""} onChange={(e) => setEditing((s) => ({ ...s, subtitle: e.target.value }))}
                                            className="w-full border border-stone-300 rounded p-2 text-sm" placeholder="Frase curta que aparece por baixo do título" maxLength={200} data-testid="blog-subtitle" />
                                        <span className="text-[11px] text-stone-500 mt-1 block">Se preenchido, substitui o excerto nas páginas do artigo.</span>
                                    </label>
                                </div>

                                <div>
                                    <label className="text-xs uppercase tracking-wider text-stone-500 block mb-2">Imagem de capa</label>
                                    {editing.cover_id ? (
                                        <div className="relative w-full max-w-md">
                                            <img src={fileUrl(editing.cover_id)} alt={editing.cover_alt || editing.title || ""} className="w-full aspect-[16/9] object-cover rounded-md" />
                                            <button type="button" onClick={() => setEditing((s) => ({ ...s, cover_id: "", cover_alt: "" }))}
                                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow"><X className="w-3.5 h-3.5" /></button>
                                        </div>
                                    ) : (
                                        <label className="flex flex-col items-center justify-center w-full max-w-md aspect-[16/9] border-2 border-dashed border-stone-300 rounded-md cursor-pointer hover:border-[hsl(var(--copper))] transition bg-stone-50" data-testid="blog-cover-upload">
                                            {uploading
                                                ? <span className="text-xs text-stone-500">A carregar…</span>
                                                : <><Upload className="w-5 h-5 text-stone-400 mb-2" /><span className="text-xs text-stone-500">Carregar imagem</span></>}
                                            <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadCover(e.target.files[0])} />
                                        </label>
                                    )}
                                    <label className="block mt-3 max-w-md">
                                        <span className="text-xs uppercase tracking-wider text-stone-500 block mb-1">Texto alternativo (alt)</span>
                                        <input
                                            value={editing.cover_alt || ""}
                                            onChange={(e) => setEditing((s) => ({ ...s, cover_alt: e.target.value }))}
                                            className="w-full border border-stone-300 rounded p-2 text-sm"
                                            placeholder="Descreva a imagem para leitores de ecrã (ex.: 'Sala de estar renovada, luz natural')"
                                            maxLength={160}
                                            data-testid="blog-cover-alt"
                                        />
                                        <span className="text-[11px] text-stone-500 mt-1 block">Se vazio, usa o título do artigo como fallback.</span>
                                    </label>
                                </div>

                                <label className="block">
                                    <span className="text-xs uppercase tracking-wider text-stone-500 block mb-1">Excerto (aparece na listagem)</span>
                                    <textarea rows={2} value={editing.excerpt} onChange={(e) => setEditing((s) => ({ ...s, excerpt: e.target.value }))}
                                        className="w-full border border-stone-300 rounded p-2 text-sm" maxLength={220} data-testid="blog-excerpt" />
                                </label>

                                <div className="pt-1">
                                    <span className="text-xs uppercase tracking-wider text-stone-500 block mb-2">Conteúdo *</span>
                                    <p className="text-[11px] text-stone-500 mb-3">Adicione blocos de conteúdo. O template do site aplica automaticamente tipografia, tamanhos e destaques — não precisa de se preocupar com formatação.</p>
                                    <BlogBlockEditor value={editing.blocks} onChange={setBlocks} types={["paragraph", "subtitle", "list", "pull_quote", "image"]} />
                                </div>

                                <div>
                                    <label className="text-xs uppercase tracking-wider text-stone-500 block mb-1">Tags</label>
                                    <div className="flex flex-wrap gap-2 mb-2">
                                        {(editing.tags || []).map((t) => (
                                            <span key={t} className="inline-flex items-center gap-1 px-2 py-1 bg-stone-100 rounded-full text-xs">
                                                {t}
                                                <button type="button" onClick={() => removeTag(t)} className="text-stone-500 hover:text-red-600"><X className="w-3 h-3" /></button>
                                            </span>
                                        ))}
                                    </div>
                                    <div className="flex gap-2">
                                        <input value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                                            placeholder="Adicionar tag e Enter"
                                            className="flex-1 border border-stone-300 rounded p-2 text-sm" />
                                        <button type="button" onClick={addTag} className="px-3 border border-stone-300 rounded text-sm">Adicionar</button>
                                    </div>
                                </div>

                                <details className="border border-stone-200 rounded p-3">
                                    <summary className="text-sm text-stone-600 cursor-pointer">Data de publicação (opcional)</summary>
                                    <div className="mt-3 space-y-1">
                                        <label className="block">
                                            <input
                                                type="datetime-local"
                                                value={editing.published_at || ""}
                                                onChange={(e) => setEditing((s) => ({ ...s, published_at: e.target.value }))}
                                                className="w-full max-w-xs border border-stone-300 rounded p-2 text-sm"
                                                data-testid="blog-published-at"
                                            />
                                        </label>
                                        <span className="text-[11px] text-stone-500 block">
                                            Se ficar vazio, ao publicar pela primeira vez usa-se a data/hora actual. Editar o artigo depois não altera a data original.
                                        </span>
                                    </div>
                                </details>

                                <details className="border border-stone-200 rounded p-3">
                                    <summary className="text-sm text-stone-600 cursor-pointer">SEO (opcional)</summary>
                                    <div className="mt-3 space-y-3">
                                        <label className="block">
                                            <span className="text-xs uppercase tracking-wider text-stone-500 block mb-1">SEO title</span>
                                            <input value={editing.seo_title} onChange={(e) => setEditing((s) => ({ ...s, seo_title: e.target.value }))}
                                                className="w-full border border-stone-300 rounded p-2 text-sm" maxLength={70} />
                                        </label>
                                        <label className="block">
                                            <span className="text-xs uppercase tracking-wider text-stone-500 block mb-1">Meta description</span>
                                            <textarea rows={2} value={editing.seo_description} onChange={(e) => setEditing((s) => ({ ...s, seo_description: e.target.value }))}
                                                className="w-full border border-stone-300 rounded p-2 text-sm" maxLength={200} />
                                        </label>
                                    </div>
                                </details>

                                <label className="flex items-center gap-2 text-sm">
                                    <input type="checkbox" checked={editing.published} onChange={(e) => setEditing((s) => ({ ...s, published: e.target.checked }))}
                                        className="accent-emerald-700" data-testid="blog-published" />
                                    Publicar imediatamente (visível no site)
                                </label>

                                <div className="flex gap-2 pt-2">
                                    <button onClick={save} className="btn-copper flex-1 justify-center" data-testid="blog-save">Guardar</button>
                                    <button onClick={() => setEditing(null)} className="px-6 py-2 border border-stone-300 rounded-full">Cancelar</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {emailFlowPost && (
                <BlogEmailFlow
                    post={emailFlowPost}
                    onClose={() => setEmailFlowPost(null)}
                    onSent={() => load()}
                />
            )}
        </div>
    );
}

function EmailStatusPill({ post }) {
    if (!post.published) return null;
    const status = post.email_status;
    if (!status || status === "not_sent") {
        return <span className="text-[10px] uppercase bg-stone-100 text-stone-500 rounded-full px-2 py-0.5" data-testid={`blog-email-status-${post.id}`}>Não enviado</span>;
    }
    const when = post.email_last_sent_at ? new Date(post.email_last_sent_at).toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" }) : "";
    const recipients = post.email_recipient_count ?? 0;
    if (status === "sent") {
        return <span className="text-[10px] uppercase bg-emerald-100 text-emerald-800 rounded-full px-2 py-0.5" data-testid={`blog-email-status-${post.id}`}>Enviado · {when} · {recipients} dest.</span>;
    }
    if (status === "sent_with_errors" || status === "queued_partial") {
        return <span className="text-[10px] uppercase bg-amber-100 text-amber-800 rounded-full px-2 py-0.5" data-testid={`blog-email-status-${post.id}`}>Enviado parcialmente · {when}</span>;
    }
    if (status === "sending") {
        return <span className="text-[10px] uppercase bg-blue-100 text-blue-800 rounded-full px-2 py-0.5" data-testid={`blog-email-status-${post.id}`}>A enviar…</span>;
    }
    if (status === "failed") {
        return <span className="text-[10px] uppercase bg-red-100 text-red-800 rounded-full px-2 py-0.5" data-testid={`blog-email-status-${post.id}`}>Falhou</span>;
    }
    return null;
}
